import { Box, Button, Checkbox } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import cx from 'classnames';
import React, { useCallback, useState } from 'react';
import { IProfileImage, ProfileImageReviewStatus } from '../../../services/types';
import useStyles from './ProfileImageItem.styles';

interface IProps {
  profileImage: IProfileImage;
  isSelected: boolean;
  isDisabled?: boolean;
  onCheckboxClick: (profileImage: IProfileImage) => void;
  onImageClick: (profileImage: IProfileImage) => void;
}

const CheckboxColorThreshold = 220;
const CheckboxAreaOffsetXY = 36;

const ProfileImageItem = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [checkboxAreaBrightness, setCheckboxAreaBrightness] = useState<number>(0);

  const onLoadImage = useCallback((e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const image = e.currentTarget;

    if (!image) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = CheckboxAreaOffsetXY;
    canvas.height = CheckboxAreaOffsetXY;

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    ctx.drawImage(image, 0, 0);

    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

    let colorSum = 0;

    for (let x = 0, len = data.length; x < len; x += 4) {
      const r = data[x];
      const g = data[x + 1];
      const b = data[x + 2];

      const avg = Math.floor((r + g + b) / 3);
      colorSum += avg;
    }

    const brightness = Math.floor(colorSum / (canvas.width * canvas.height));

    setCheckboxAreaBrightness(brightness);
    setIsLoaded(true);
  }, []);

  const { profileImage, isSelected, onCheckboxClick, onImageClick } = props;

  const handleCheckboxChange = useCallback(() => {
    onCheckboxClick(profileImage);
  }, [onCheckboxClick, profileImage]);

  const handleImageClick = useCallback(() => {
    onImageClick(profileImage);
  }, [onImageClick, profileImage]);

  return (
    <Box className={classes.wrap}>
      {isSelected && (
        <Button onClick={handleImageClick} className={classes.backdrop}>
          <span />
        </Button>
      )}
      {!isSelected && profileImage.review.status !== ProfileImageReviewStatus.NotReviewed && (
        <Button onClick={handleImageClick} className={classes.reviewed}>
          <span />
        </Button>
      )}
      <Checkbox
        checked={isSelected}
        onChange={handleCheckboxChange}
        className={classes.checkbox}
        color="primary"
        disabled={props.isDisabled}
        classes={{
          root: cx({
            [classes.lightCheckbox]: checkboxAreaBrightness <= CheckboxColorThreshold,
            [classes.darkCheckbox]: checkboxAreaBrightness > CheckboxColorThreshold,
          }),
        }}
        checkedIcon={
          <CheckBoxIcon
            style={{
              backgroundImage:
                'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+ip1sAAAAASUVORK5CYII=)',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '15px 15px',
              backgroundPosition: '4px 4px',
            }}
          />
        }
      />
      {profileImage.review.status !== ProfileImageReviewStatus.NotReviewed && (
        <Box
          className={cx({
            [classes.reviewText]: true,
            [classes.approve]: profileImage.review.status === ProfileImageReviewStatus.Approved,
            [classes.pending]: profileImage.review.status === ProfileImageReviewStatus.Pending,
            [classes.blocked]: profileImage.review.status === ProfileImageReviewStatus.Blocked,
          })}
        >
          {profileImage.review.status}
        </Box>
      )}
      <Button onClick={handleImageClick} className={classes.imageButton} disabled={props.isDisabled}>
        <img
          src={profileImage.photoUrl}
          className={cx({ [classes.image]: true, [classes.loaded]: isLoaded })}
          onLoad={onLoadImage}
          crossOrigin={'anonymous'}
        />
      </Button>
    </Box>
  );
};

export default React.memo(ProfileImageItem);
